import { useState, useEffect, useRef } from 'react'
import { ImagePlus } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { useWorkspace } from '../lib/WorkspaceContext'
import { aISO, combinarItemsTracking } from '../lib/tracking'
import './Inicio.css'

const LABEL_TIPO = { frase: 'Frase del día', testimonio: 'Testimonio', mensaje: 'Mensaje de tu equipo' }

export default function Inicio({ usuario, onNavegar }) {
  const { workspaceActivo } = useWorkspace()
  const [frases, setFrases] = useState([])
  const [testimonios, setTestimonios] = useState([])
  const [mensajes, setMensajes] = useState([])
  const [itemIdx, setItemIdx] = useState(0)
  const [fotoUrl, setFotoUrl] = useState(null)
  const [subiendo, setSubiendo] = useState(false)
  const [resumenHabitos, setResumenHabitos] = useState(null)
  const [proximoTurno, setProximoTurno] = useState(null)
  const inputFotoRef = useRef(null)

  useEffect(() => {
    cargarDatos()
  }, [workspaceActivo?.id])

  useEffect(() => {
    if (usuario?.id) cargarResumenHabitos()
  }, [usuario?.id])

  useEffect(() => {
    if (!workspaceActivo?.id || !usuario?.id) return
    supabase.from('turnos')
      .select('id, inicio, estado, modalidad, tipos_consulta(nombre)')
      .eq('workspace_id', workspaceActivo.id)
      .eq('paciente_id', usuario.id)
      .in('estado', ['pendiente', 'confirmado'])
      .gte('inicio', new Date().toISOString())
      .order('inicio', { ascending: true })
      .limit(1)
      .then(({ data }) => setProximoTurno(data?.[0] || null))
  }, [workspaceActivo?.id, usuario?.id])

  const cargarDatos = async () => {
    if (!workspaceActivo) return
    const [{ data: frasesAdmin }, { data: frasesUsuario }, { data: fotos }, { data: testimoniosData }, { data: mensajesData }] = await Promise.all([
      supabase.from('frases_motivacionales').select('*').eq('tipo', 'admin').eq('activa', true).eq('workspace_id', workspaceActivo.id),
      supabase.from('frases_motivacionales').select('*').eq('usuario_id', usuario.id),
      supabase.from('fotos_inspiracionales').select('foto_url').eq('usuario_id', usuario.id).limit(1),
      supabase.from('testimonios').select('*').eq('workspace_id', workspaceActivo.id).eq('autorizado', true),
      supabase.from('mensajes_profesional').select('*').eq('workspace_id', workspaceActivo.id).eq('activo', true)
    ])

    const todasFrases = [...(frasesAdmin || []), ...(frasesUsuario || [])]
    setFrases(todasFrases)
    setTestimonios(testimoniosData || [])
    setMensajes(mensajesData || [])

    const total = todasFrases.length + (testimoniosData?.length || 0) + (mensajesData?.length || 0)
    if (total > 0) setItemIdx(Math.floor(Math.random() * total))

    if (fotos && fotos.length > 0) setFotoUrl(fotos[0].foto_url)
  }

  // Misma fuente que la grilla de Hábitos (combinarItemsTracking, en
  // lib/tracking.js) para que este número nunca quede desalineado del
  // "Total del día" que muestra Habitos.js — acá antes había un cálculo
  // propio contra habitos_config solo, que se había quedado sin sumar
  // suplementos.
  const cargarResumenHabitos = async () => {
    const hoy = aISO(new Date())
    const [{ data: configs }, { data: suplementos }] = await Promise.all([
      supabase.from('habitos_config')
        .select('id, meta_valor, semana_inicio, habitos_catalogo(nombre, tipo, campo_referencia)')
        .eq('paciente_id', usuario.id).eq('activo', true),
      supabase.from('suplementos_config')
        .select('id, created_at, suplementos_catalogo(nombre)')
        .eq('paciente_id', usuario.id).eq('activo_paciente', true)
    ])

    const habitosActivos = configs || []
    const suplementosActivos = suplementos || []
    if (habitosActivos.length === 0 && suplementosActivos.length === 0) { setResumenHabitos({ cumplidos: 0, total: 0 }); return }

    const tieneBaseDiario = habitosActivos.some(c => c.habitos_catalogo.tipo === 'base_diario')
    const idsPersonalizados = habitosActivos.filter(c => c.habitos_catalogo.tipo === 'personalizado').map(c => c.id)
    const idsSuplementos = suplementosActivos.map(s => s.id)

    const [{ data: controlHoy }, { data: checksHabitosHoy }, { data: checksSuplementosHoy }] = await Promise.all([
      tieneBaseDiario
        ? supabase.from('control_diario').select('horas_sueno, litros_agua, registro_comidas(tipo_comida, descripcion, realizada)').eq('usuario_id', usuario.id).eq('fecha', hoy).maybeSingle()
        : Promise.resolve({ data: null }),
      idsPersonalizados.length > 0
        ? supabase.from('registro_habito_personalizado').select('habitos_config_id, cumplido').eq('fecha', hoy).in('habitos_config_id', idsPersonalizados)
        : Promise.resolve({ data: [] }),
      idsSuplementos.length > 0
        ? supabase.from('registro_suplemento').select('suplementos_config_id, cumplido').eq('fecha', hoy).in('suplementos_config_id', idsSuplementos)
        : Promise.resolve({ data: [] })
    ])

    const controlDiario = controlHoy ? { [hoy]: controlHoy } : {}
    const checksHabitos = {}
    for (const c of checksHabitosHoy || []) checksHabitos[`${c.habitos_config_id}:${hoy}`] = c.cumplido
    const checksSuplementos = {}
    for (const c of checksSuplementosHoy || []) checksSuplementos[`${c.suplementos_config_id}:${hoy}`] = c.cumplido

    const itemsTracking = combinarItemsTracking(habitosActivos, suplementosActivos, controlDiario, checksHabitos, checksSuplementos, null)
    const activosHoy = itemsTracking.filter(item => hoy >= item.fechaDesde)
    const cumplidos = activosHoy.filter(item => item.cumplido(hoy)).length

    setResumenHabitos({ cumplidos, total: activosHoy.length })
  }

  const itemsRotativos = [
    ...frases.map(f => ({ id: `frase-${f.id}`, tipo: 'frase', texto: f.texto, autor: f.autor, esPropia: f.tipo === 'usuario', rawId: f.id })),
    ...testimonios.map(t => ({ id: `testimonio-${t.id}`, tipo: 'testimonio', texto: t.texto, autor: t.autor_nombre })),
    ...mensajes.map(m => ({ id: `mensaje-${m.id}`, tipo: 'mensaje', texto: m.texto }))
  ]
  const itemActual = itemsRotativos[itemIdx]

  const siguienteItem = () => {
    if (itemsRotativos.length > 1) setItemIdx(i => (i + 1) % itemsRotativos.length)
  }

  const subirFoto = async (e) => {
    const file = e.target.files[0]
    if (!file) return
    setSubiendo(true)
    const ext = file.name.split('.').pop()
    const path = `${usuario.id}/inspiracion.${ext}`

    // Bug #46: acá no se chequeaba el resultado de upload()/upsert() — si
    // cualquiera de los dos fallaba, el código seguía de largo igual y
    // terminaba mostrando una URL rota (o la vieja) sin avisar nada.
    const { error: errorSubida } = await supabase.storage.from('fotos-inspiracionales').upload(path, file, { upsert: true })
    if (errorSubida) { alert('No se pudo subir la foto. Probá de nuevo.'); setSubiendo(false); return }

    const { data: { publicUrl } } = supabase.storage.from('fotos-inspiracionales').getPublicUrl(path)
    // upsert:true pisa el mismo archivo — sin este cache-buster, el browser
    // puede seguir sirviendo la imagen vieja desde caché para esa misma URL.
    const urlVersionada = `${publicUrl}?v=${Date.now()}`

    const { error: errorGuardado } = await supabase.from('fotos_inspiracionales')
      .upsert({ usuario_id: usuario.id, foto_url: urlVersionada }, { onConflict: 'usuario_id' })
    if (errorGuardado) { alert('La foto se subió pero no se pudo guardar. Probá de nuevo.'); setSubiendo(false); return }

    setFotoUrl(urlVersionada)
    setSubiendo(false)
  }

  const eliminarFrase = async (id) => {
    await supabase.from('frases_motivacionales').delete().eq('id', id).eq('tipo', 'usuario')
    setFrases(prev => prev.filter(f => f.id !== id))
    setItemIdx(0)
  }

  const nombre = usuario?.user_metadata?.nombre || usuario?.email?.split('@')[0] || 'Hola'

  return (
    <div className="inicio-page">
      {/* FOTO INSPIRACIONAL */}
      <div className="inicio-foto-wrap" onClick={() => !subiendo && inputFotoRef.current?.click()}>
        {fotoUrl ? (
          <img src={fotoUrl} alt="Inspiración" className="inicio-foto-img" onError={() => setFotoUrl(null)} />
        ) : (
          <div className="inicio-foto-placeholder">
            <ImagePlus className="inicio-foto-icono" size={32} strokeWidth={1.75} />
            <p>Subí tu foto inspiracional</p>
          </div>
        )}
        <div className="inicio-foto-overlay">
          {subiendo ? '⏳ Subiendo...' : '📷 Cambiar foto'}
        </div>
        <input ref={inputFotoRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={subirFoto} />
      </div>

      <div className="inicio-contenido">
        <p className="inicio-saludo">Hola, {nombre} 🌿</p>
        <p className="inicio-proposito">Tu espacio del día: inspiración, hábitos y tu progreso en un vistazo.</p>
        {workspaceActivo?.nombre && <p className="inicio-seccion-label">{workspaceActivo.nombre}</p>}

        {/* PRÓXIMO TURNO */}
        {proximoTurno && (
          <div className="inicio-seccion">
            <p className="inicio-seccion-label">📅 Tu próximo turno</p>
            <button className="inicio-habitos-card" onClick={() => onNavegar('turnos')}>
              <p className="inicio-habitos-texto">
                {new Intl.DateTimeFormat('es-AR', { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' }).format(new Date(proximoTurno.inicio))}
              </p>
              <p className="inicio-habitos-hint">
                {proximoTurno.tipos_consulta?.nombre}
                {proximoTurno.modalidad === 'virtual' ? ' · por videollamada' : ' · presencial'} · Ver turnos →
              </p>
            </button>
          </div>
        )}

        {/* RESUMEN DE HÁBITOS — bloque propio, separado del de frases */}
        {resumenHabitos && resumenHabitos.total > 0 && (
          <div className="inicio-seccion">
            <p className="inicio-seccion-label">🔁 Hábitos de hoy</p>
            <button className="inicio-habitos-card" onClick={() => onNavegar('habitos')}>
              <p className="inicio-habitos-texto">Hoy cumpliste {resumenHabitos.cumplidos}/{resumenHabitos.total} hábitos</p>
              <p className="inicio-habitos-hint">Ver Hábitos →</p>
            </button>
          </div>
        )}

        {/* TARJETA ROTATIVA (frases, testimonios, mensajes del profesional) */}
        <div className="inicio-seccion">
          <p className="inicio-seccion-label">{LABEL_TIPO[itemActual?.tipo] || 'Para vos hoy'}</p>
          {itemActual ? (
            <div className="inicio-frase-card" onClick={siguienteItem}>
              <p className="inicio-frase-texto">"{itemActual.texto}"</p>
              {itemActual.autor && <p className="inicio-frase-autor">— {itemActual.autor}</p>}
              {itemsRotativos.length > 1 && <p className="inicio-frase-hint">Tocá para ver otra →</p>}
              {itemActual.esPropia && (
                <button
                  className="inicio-frase-delete"
                  onClick={e => { e.stopPropagation(); eliminarFrase(itemActual.rawId) }}
                >✕</button>
              )}
            </div>
          ) : (
            <div className="inicio-frase-vacia">
              <p>Agregá frases que te inspiren para encontrarlas acá cuando las necesitás.</p>
            </div>
          )}
        </div>

        {/* MIS FRASES */}
        {frases.filter(f => f.tipo === 'usuario').length > 0 && (
          <div className="inicio-seccion">
            <p className="inicio-seccion-label">Mis frases ({frases.filter(f => f.tipo === 'usuario').length})</p>
            <div className="inicio-frases-lista">
              {frases.filter(f => f.tipo === 'usuario').map((f, i) => (
                <div key={f.id} className="inicio-frase-item">
                  <p>"{f.texto}"</p>
                  <button onClick={() => eliminarFrase(f.id)}>✕</button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
